import { createElement } from 'react';
import { Icon, Stack, SubTitle, Text, Title } from '@appcues/sonar';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons/faArrowUpRightFromSquare';
import styled from 'styled-components';
import { Header, Page, PageBody } from 'studio5/components';
import {
  BannerGroup,
  FeatureBanner,
  PreviewBanner,
  SpoofBanner,
} from 'studio5/components/Banners';
import ErrorBoundary, {
  RouteLevelErrorPage,
} from 'studio5/components/ErrorBoundary';
import { useTitle } from 'studio5/hooks';
import type { LinkOutContent } from 'studio5/lib/link-outs/types';
import { getAgentStudioUrl } from 'studio5/lib/platform';
import ButtonLink from './ButtonLink';

const Card = styled(Stack)`
  margin: var(--spacing-x-x-large) auto 0;
  max-width: 448px;
  text-align: center;
`;

const IconTile = styled.div`
  display: grid;
  place-items: center;
  width: 48px;
  height: 48px;
  border-radius: var(--border-radius-large);
  background: var(--background-level-2);
  color: var(--foreground-secondary);
`;

type Props = {
  /** Agent Studio path the button opens, e.g. `/audiences/segments/abc`. */
  path: string;
  content: LinkOutContent;
};

/**
 * Rendered by the shell outside both sub-apps, so it brings the banners and
 * error boundary that studio5's PageLayout would otherwise supply.
 */
export default function AgentStudioLinkOut({
  path,
  content: { title, heading, message, icon, secondaryAction: SecondaryAction },
}: Props) {
  useTitle(`${title} | Appcues`);

  return (
    <ErrorBoundary FallbackComponent={RouteLevelErrorPage}>
      <BannerGroup>
        <PreviewBanner />
        <SpoofBanner />
        <FeatureBanner />
      </BannerGroup>
      <Page>
        <Header titleRow={<Title>{title}</Title>} />
        <PageBody>
          <Card xAlign="center" spacing="regular">
            <IconTile>
              {typeof icon === 'function' ? (
                createElement(icon)
              ) : (
                <Icon icon={icon} size="large" />
              )}
            </IconTile>
            <SubTitle>{heading}</SubTitle>
            <Text colorToken="foreground-secondary">{message}</Text>
            <ButtonLink
              forwardedAs="a"
              href={`${getAgentStudioUrl()}${path}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in new App
              <Icon icon={faArrowUpRightFromSquare} size="small" />
            </ButtonLink>
            {SecondaryAction && <SecondaryAction />}
          </Card>
        </PageBody>
      </Page>
    </ErrorBoundary>
  );
}
